import {
  Box,
  Flex,
  Heading,
  Skeleton,
  VStack,
  HStack,
  Text,
  Badge,
  useColorModeValue,
  SimpleGrid,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import React from "react";
import ListOfLeave from "./ListOfLeave";
import { FiFilter } from "react-icons/fi";

const LeaveTable = ({ employeeData = [], loading, refetchLeave }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bgColor = useColorModeValue("white", "gray.800");

  if (loading) {
    return (
      <Box p={6}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Box key={i} bg="white" p={6} borderRadius="16px" shadow="sm" border="1px solid" borderColor="gray.100">
              <Skeleton height="20px" mb={3} />
              <Skeleton height="16px" mb={2} />
              <Skeleton height="16px" mb={2} />
              <Skeleton height="16px" mb={4} />
              <Flex gap={2}>
                <Skeleton height="32px" flex={1} />
                <Skeleton height="32px" flex={1} />
        </Flex>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!loading && !employeeData?.length) {
    return (
      <Box p={8} textAlign="center">
        <VStack spacing={4}>
          <Box
            p={4}
            borderRadius="50%"
            bg="gray.100"
            color="gray.500"
            fontSize="2xl"
          >
            📋
          </Box>
          <VStack spacing={2}>
            <Text fontSize="lg" fontWeight="600" color="gray.700">
              No Leave Requests
            </Text>
            <Text color="gray.500" fontSize="sm">
              There are currently no leave requests to review
            </Text>
          </VStack>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      {/* Table Header */}
      <Flex 
        justifyContent="space-between" 
        alignItems="center" 
        p={6} 
        borderBottom="1px solid" 
        borderColor="gray.200"
        bg={bgColor}
      >
        <VStack align="start" spacing={1}>
          <Heading size="md" color="gray.900" fontWeight="600">
            Leave Requests
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {employeeData.length} requests found
          </Text>
        </VStack>
        <IconButton
          icon={<FiFilter />}
          variant="ghost"
          onClick={onOpen}
          aria-label="Filter requests"
          color="gray.600"
          _hover={{ bg: "gray.100" }}
        />
      </Flex>

      {/* Leave Cards */}
      <VStack spacing={4} p={6} align="stretch">
        {employeeData.map((item) => (
          <LeaveCard key={item?.leave_id} item={item} refetchLeave={refetchLeave} />
        ))}
      </VStack>

      {/* Filter Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Filter Leave Requests</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text color="gray.600">Filter options coming soon...</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Leave Card Component
const LeaveCard = ({ item, refetchLeave }) => {
  const {
    approved_by_email,
    reason,
    leave_end,
    leave_start,
    entry_type,
    full_name,
    employee_id,
    approval_timestamp,
    status,
    assigned_reporting_senior_name,
    reminder_count,
    last_reminder_at,
  } = item;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      case 'pending':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="16px"
      shadow="sm"
      border="1px solid"
      borderColor="gray.200"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
    >
      <VStack spacing={4} align="stretch">
        {/* Header */}
        <Flex justifyContent="space-between" alignItems="start">
          <VStack align="start" spacing={1}>
            <Text fontWeight="600" color="gray.900" fontSize="md">
              {full_name}
            </Text>
            <Text color="gray.600" fontSize="sm">
              ID: {employee_id} • {entry_type}
            </Text>
          </VStack>
          <Badge
            colorScheme={getStatusColor(status)}
            variant="subtle"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="sm"
          >
            {status}
          </Badge>
        </Flex>

        {/* Leave Details */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Leave Period
            </Text>
            <Text fontSize="sm" color="gray.900">
              {new Date(leave_start).toLocaleDateString()} - {new Date(leave_end).toLocaleDateString()}
            </Text>
          </VStack>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
                Reason
            </Text>
            <Text fontSize="sm" color="gray.900" noOfLines={2}>
              {reason}
            </Text>
          </VStack>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Approver (assigned)
            </Text>
            <Text fontSize="sm" color="gray.900">
              {assigned_reporting_senior_name || "—"}
            </Text>
          </VStack>
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              Reminders
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              <Badge colorScheme={(reminder_count ?? 0) > 0 ? "blue" : "gray"} variant="subtle">
                {reminder_count ?? 0} sent
              </Badge>
              {last_reminder_at && (
                <Text fontSize="xs" color="gray.600">
                  Last: {new Date(last_reminder_at).toLocaleString()}
                </Text>
              )}
            </HStack>
          </VStack>
        </SimpleGrid>

        {/* Approval Info */}
        {(approved_by_email || approval_timestamp) && (
          <Box p={3} bg="gray.50" borderRadius="8px">
            <Text fontSize="sm" color="gray.600">
              Approved by: {approved_by_email || 'N/A'}
            </Text>
            {approval_timestamp && (
              <Text fontSize="sm" color="gray.600">
                Date: {new Date(approval_timestamp).toLocaleDateString()}
              </Text>
            )}
          </Box>
        )}

        {/* Actions */}
        <Flex gap={3} flexWrap="wrap">
          <ListOfLeave item={item} refetchLeave={refetchLeave} />
        </Flex>
      </VStack>
    </Box>
  );
};

export default LeaveTable;
